import { useState } from 'react';
import type { PopulatedMenu } from 'root/apiTypes';
import type { NavigateToFn } from 'root/clientTypes';
import type { DropdownOptionProps } from 'wix-ui-tpa/cssVars';
import { useExperiments, type ILocation, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from 'root/components/Menus/stylesParams';
import { useGetDisplayStylesParams } from 'root/hooks/useGetDisplayStylesParams';
import { useSharedNavigationLogic } from 'root/components/Menus/NavigationWithSections/Navigation.shared.logic';

type UseNavigationWithSectionsLogicParams = {
  menus: PopulatedMenu[];
  setActiveItem: (index: number) => void;
  navigateTo: NavigateToFn;
  currentUrl: ILocation['url'];
  activeMenuIndex: number;
};

export const useNavigationWithSectionsLogic = ({
  menus,
  setActiveItem,
  navigateTo,
  currentUrl = '',
  activeMenuIndex,
}: UseNavigationWithSectionsLogicParams) => {
  const { selectMenu } = useSharedNavigationLogic({
    menus,
    setActiveItem,
    navigateTo,
    currentUrl,
  });
  const initialSectionTabId = currentUrl?.split('#section-')?.[1];
  const initialSectionIndex =
    menus.length > 0 && activeMenuIndex >= 0 && activeMenuIndex < menus.length
      ? menus[activeMenuIndex]?.sections?.findIndex((section) => section.id === initialSectionTabId)
      : -1;
  const initialSectionTabIndex = initialSectionIndex !== -1 ? initialSectionIndex : 0;
  const [activeSection, setActiveSection] = useState(initialSectionTabIndex);
  const { showTabs } = useGetDisplayStylesParams();
  const styles = useStyles();
  const navigationDropdownStyle = styles.get(stylesParams.navigationDropdownStyle);

  const { t } = useTranslation();

  const { experiments } = useExperiments();
  const isDropdownBottomExperimentEnabled = experiments.enabled('specs.restaurants.menusOoiDropdownBottom');

  const shouldShowMenusDropdown = menus.length > 1 && showTabs;

  const onClickNavigationDropdownOption = (option: DropdownOptionProps) => {
    selectMenu(Number(option.id));
  };

  const dropdownOptions = menus.map((menu, index): DropdownOptionProps => {
    return {
      id: index.toString(),
      value: menu?.name,
      isSelectable: true,
    };
  });
  return {
    dropdownOptions,
    onClickNavigationDropdownOption,
    navigationDropdownStyle,
    dropdowmPlacement: isDropdownBottomExperimentEnabled ? 'bottom' : undefined,
    dropdownFlip: isDropdownBottomExperimentEnabled ? false : undefined,
    t,
    shouldShowMenusDropdown,
    activeSection,
    setActiveSection,
    showSectionNavigation: menus[activeMenuIndex]?.sections?.length > 0,
  };
};
